import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Phone, Search, Upload, Users, Plus, MoreHorizontal, Ban, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import type { Lead, LeadStatus } from '@fenix/shared';
import { LEAD_STATUSES } from '@fenix/shared';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { StatusBadge } from '@/components/StatusBadge';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { DataTablePagination } from '@/components/DataTablePagination';
import { EmptyState } from '@/components/EmptyState';
import { leadsApi } from '@/lib/api/endpoints';
import { ApiError } from '@/lib/api/client';
import { LEAD_STATUS_LABEL, LEAD_STATUS_VARIANT, fmtPhone, fmtRelative } from '@/lib/format';
import { ImportLeadsDialog } from './ImportLeadsDialog';
import { LeadFormDialog } from './LeadFormDialog';
import { CallNowDialog } from './CallNowDialog';
import { useCan } from '@/features/auth/store';

const ALL = '__all__';

export function LeadsPage() {
  const [params, setParams] = useSearchParams();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const canEdit = useCan('leads.manage');
  const canCall = useCan('leads.call');
  const canDelete = useCan('leads.delete');

  const query = {
    page: Number(params.get('page') ?? 1),
    pageSize: 25,
    search: params.get('search') ?? undefined,
    status: params.get('status') ?? undefined,
    sourceSlug: params.get('source') ?? undefined,
    productCodename: params.get('product') ?? undefined,
    country: params.get('country') ?? undefined,
    phoneValid: params.get('phone') ?? undefined,
  };
  const set = (k: string, v: string | number | undefined) => {
    const next = new URLSearchParams(params);
    if (v === undefined || v === '' || v === ALL) next.delete(k);
    else next.set(k, String(v));
    if (k !== 'page') next.delete('page');
    setParams(next, { replace: true });
  };

  const leads = useQuery({
    queryKey: ['leads', query],
    queryFn: () => leadsApi.list(query),
    placeholderData: (p) => p,
  });
  const facets = useQuery({
    queryKey: ['leads', 'facets'],
    queryFn: leadsApi.facets,
    staleTime: 60_000,
  });

  const [importOpen, setImportOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [callLead, setCallLead] = useState<Lead | null>(null);

  const statusMut = useMutation({
    mutationFn: ({ id, status }: { id: string; status: LeadStatus }) =>
      leadsApi.setStatus(id, status),
    onSuccess: () => {
      toast.success('Status atualizado');
      void qc.invalidateQueries({ queryKey: ['leads'] });
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Erro'),
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => leadsApi.remove(id),
    onSuccess: () => {
      toast.success('Lead excluído');
      void qc.invalidateQueries({ queryKey: ['leads'] });
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Erro'),
  });

  return (
    <div>
      <PageHeader
        title="Leads"
        description="Carrinhos abandonados prontos para recuperação"
        actions={
          canEdit && (
            <>
              <Button variant="outline" onClick={() => setImportOpen(true)}>
                <Upload /> Importar CSV
              </Button>
              <Button onClick={() => setFormOpen(true)}>
                <Plus /> Novo lead
              </Button>
            </>
          )
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative w-full sm:w-64">
          <Search className="pointer-events-none absolute left-2.5 top-2.5 size-4 text-muted-foreground" />
          <Input
            placeholder="Nome, e-mail ou telefone"
            className="pl-8"
            defaultValue={query.search ?? ''}
            onKeyDown={(e) =>
              e.key === 'Enter' && set('search', (e.target as HTMLInputElement).value)
            }
            onBlur={(e) => e.target.value !== (query.search ?? '') && set('search', e.target.value)}
          />
        </div>
        <Select value={query.status ?? ALL} onValueChange={(v) => set('status', v)}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Todos os status</SelectItem>
            {LEAD_STATUSES.map((s) => (
              <SelectItem key={s} value={s}>
                {LEAD_STATUS_LABEL[s]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={query.sourceSlug ?? ALL} onValueChange={(v) => set('source', v)}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Origem" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Todas as origens</SelectItem>
            {facets.data?.sources.map((f) => (
              <SelectItem key={f.value} value={f.value}>
                {f.value} ({f.count})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={query.productCodename ?? ALL} onValueChange={(v) => set('product', v)}>
          <SelectTrigger className="w-44">
            <SelectValue placeholder="Produto" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Todos os produtos</SelectItem>
            {facets.data?.products.map((f) => (
              <SelectItem key={f.value} value={f.value}>
                {f.value} ({f.count})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={query.country ?? ALL} onValueChange={(v) => set('country', v)}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="País" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Todos os países</SelectItem>
            {facets.data?.countries.map((f) => (
              <SelectItem key={f.value} value={f.value}>
                {f.value} ({f.count})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={query.phoneValid ?? ALL} onValueChange={(v) => set('phone', v)}>
          <SelectTrigger className="w-44">
            <SelectValue placeholder="Telefone" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Todos os telefones</SelectItem>
            <SelectItem value="valid">Telefone válido</SelectItem>
            <SelectItem value="invalid">Telefone inválido</SelectItem>
            <SelectItem value="pending">Ainda não validado</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-lg border bg-card">
        {leads.isLoading ? (
          <div className="space-y-2 p-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : leads.data && leads.data.items.length ? (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Lead</TableHead>
                  <TableHead>Telefone</TableHead>
                  <TableHead>Produto</TableHead>
                  <TableHead>País</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-center">Tent.</TableHead>
                  <TableHead>Recebido</TableHead>
                  <TableHead className="w-24 text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {leads.data.items.map((l) => (
                  <TableRow
                    key={l.id}
                    className="cursor-pointer"
                    onClick={() => navigate(`/leads/${l.id}`)}
                  >
                    <TableCell>
                      <p className="font-medium">
                        {l.name || <span className="text-muted-foreground">Sem nome</span>}
                      </p>
                      <p className="text-xs text-muted-foreground">{l.email ?? '—'}</p>
                    </TableCell>
                    <TableCell className="tabular-nums">
                      {fmtPhone(l.phoneE164)}
                      {l.phoneValid === false && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Badge variant="destructive" className="ml-2 align-middle">
                              inválido
                            </Badge>
                          </TooltipTrigger>
                          <TooltipContent>
                            Número que não existe na numeração do país. Campanhas, piloto e
                            &quot;Ligar agora&quot; não ligam — corrija o cadastro.
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>
                      <p className="text-sm">{l.product?.name ?? l.productCodename ?? '—'}</p>
                      <p className="text-xs text-muted-foreground">{l.sourceSlug}</p>
                    </TableCell>
                    <TableCell className="text-sm">{l.country ?? '—'}</TableCell>
                    <TableCell>
                      <StatusBadge
                        label={LEAD_STATUS_LABEL[l.status]}
                        className={LEAD_STATUS_VARIANT[l.status]}
                      />
                    </TableCell>
                    <TableCell className="text-center tabular-nums">{l.attempts}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {fmtRelative(l.receivedAt)}
                    </TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex justify-end gap-1">
                        {canCall && (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={l.status === 'do_not_call' || l.status === 'calling'}
                            onClick={() => setCallLead(l)}
                          >
                            <Phone /> Ligar
                          </Button>
                        )}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button size="icon" variant="ghost" className="size-8">
                              <MoreHorizontal />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem asChild>
                              <Link to={`/leads/${l.id}`}>Ver detalhes</Link>
                            </DropdownMenuItem>
                            {canEdit && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  onClick={() => statusMut.mutate({ id: l.id, status: 'new' })}
                                >
                                  Marcar como novo
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() =>
                                    statusMut.mutate({ id: l.id, status: 'recovered' })
                                  }
                                >
                                  Marcar como recuperado
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  className="text-destructive"
                                  onClick={() =>
                                    statusMut.mutate({ id: l.id, status: 'do_not_call' })
                                  }
                                >
                                  <Ban /> Não ligar mais
                                </DropdownMenuItem>
                                {canDelete && (
                                  <DropdownMenuItem
                                    className="text-destructive"
                                    onClick={() =>
                                      confirm('Excluir este lead?') && deleteMut.mutate(l.id)
                                    }
                                  >
                                    <Trash2 /> Excluir
                                  </DropdownMenuItem>
                                )}
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <DataTablePagination
              page={leads.data.page}
              pageSize={leads.data.pageSize}
              total={leads.data.total}
              onPageChange={(p) => set('page', p)}
            />
          </>
        ) : (
          <div className="p-6">
            <EmptyState
              icon={Users}
              title="Nenhum lead encontrado"
              description="Importe um CSV exportado da tabela de carrinhos abandonados ou cadastre manualmente."
              action={
                canEdit && (
                  <Button onClick={() => setImportOpen(true)}>
                    <Upload /> Importar CSV
                  </Button>
                )
              }
            />
          </div>
        )}
      </div>

      <ImportLeadsDialog open={importOpen} onOpenChange={setImportOpen} />
      <LeadFormDialog open={formOpen} onOpenChange={setFormOpen} />
      <CallNowDialog lead={callLead} onOpenChange={(o) => !o && setCallLead(null)} />
    </div>
  );
}
